import React, {useMemo} from 'react';
import {Outlet, useLocation} from 'react-router-dom';
import Footer from '../../components/layout/Footer';
import AppLayout from '../../components/layout/AppLayout';
import Sidebar from '../../components/guide/Sidebar';
import GuideMobileHeader from '../../components/guide/GuideMobileHeader';
import {GUIDE_NAV_ITEMS, GuideNavItem, MOBILE_NAV_ITEMS} from '../../components/guide/navConfig';

type GuideNavMatch = {
    item: GuideNavItem;
    parent?: GuideNavItem;
};

const normalizePath = (path: string) => {
    if (!path) {
        return '/';
    }
    const trimmed = path.replace(/\/+$/, '');
    return trimmed === '' ? '/' : trimmed;
};

const findActiveNavItem = (
    items: GuideNavItem[],
    targetPath: string,
    parent?: GuideNavItem
): GuideNavMatch | undefined => {
    for (const item of items) {
        const itemPath = item.href?.startsWith('/') ? normalizePath(item.href) : undefined;
        if (itemPath && itemPath === targetPath) {
            return {item, parent};
        }
        if (item.items?.length) {
            const match = findActiveNavItem(item.items, targetPath, item);
            if (match) {
                return match;
            }
        }
    }
    return undefined;
};

type MobileNavLink = {
    id: string;
    label: string;
    href: string;
    isExternal: boolean;
};

const toMobileNavLinks = (items: GuideNavItem[]): MobileNavLink[] =>
    items.flatMap((item) => {
        const childLinks = item.items ? toMobileNavLinks(item.items) : [];
        if (!item.href) {
            return childLinks;
        }

        const isExternal = !item.href.startsWith('/');
        return [
            {
                id: item.id,
                label: item.label,
                href: item.href,
                isExternal,
            },
            ...childLinks,
        ];
    });

const GuideLayout: React.FC = () => {
    const location = useLocation();
    const activeMatch = useMemo(() => {
        const currentPath = normalizePath(location.pathname);
        return findActiveNavItem(GUIDE_NAV_ITEMS, currentPath);
    }, [location.pathname]);

    const activeItem = activeMatch?.item;
    const activeParent = activeMatch?.parent;

    const mobileHeading = useMemo(() => {
        let mobileLabel = activeParent?.label ?? activeItem?.label ?? '분양의신 가이드';

        if (location.pathname === '/guide/principles/premium') {
            return '01 프리미엄 콘텐츠 &\n성과 중심 광고 솔루션'
        } else if (location.pathname === '/guide/principles/all-in-one') {
            return '02 올인원(All-in-One)\\n대행 시스템'
        } else if (location.pathname === '/guide/principles/profit') {
            return '03 고객이 실제로\\n돈을 벌게 만드는 광고'
        } else if (location.pathname === '/guide/content/banner') {
            return '배너 & 홈페이지 콘텐츠'
        } else if (location.pathname === '/guide/content/cf') {
            return '분양의신 CF 배너 시스템'
        }

        return mobileLabel;
    }, [activeParent, activeItem, location.pathname])
    const mobileSubTitle = activeParent?.subTitle ?? activeItem?.subTitle ?? '분양의신 가이드';

    const mobileTabs = useMemo(() => toMobileNavLinks(MOBILE_NAV_ITEMS), []);

    const mobileActiveId = useMemo(() => {
        const currentPath = normalizePath(location.pathname);
        const matchedMobileTab = mobileTabs.find((tab) => {
            if (tab.isExternal) {
                return false;
            }
            return normalizePath(tab.href) === currentPath;
        });

        if (matchedMobileTab) {
            return matchedMobileTab.id;
        }

        if (activeParent) {
            return activeParent.id;
        }

        return activeItem?.id;
    }, [location.pathname, mobileTabs, activeParent, activeItem]);

    return (
        <div className="min-h-screen bg-white text-slate-900">
            <AppLayout
                sidebar={<Sidebar activeId={activeItem?.id}/>}
                mobileHeader={
                    mobileTabs.length > 0
                        ? (
                            <GuideMobileHeader
                                subtitle={mobileSubTitle}
                                title={mobileHeading}
                                tabs={mobileTabs}
                                activeId={mobileActiveId}
                            />
                        )
                        : undefined
                }
            >
                <Outlet/>
            </AppLayout>
            <Footer/>
        </div>
    );
};

export default GuideLayout;
